import { useEffect } from 'react';
import { fetchSettings } from '@connect-x-app/hybrid-data-access';
import { PushNotificationGroup, PushNotificationSetting, ThemePreference, TimeZone } from '@connect-x-app/shared-domain-models';
import {
  getPushNotificationGroupKeys,
  getPushNotificationKeysToUpdate,
  sortPushNotificationSettings,
} from '@connect-x-app/shared-utils';

import { useSettingsStore } from '../stores';
import { useGetSettings, useUpdateSettings } from './queries';

export const useSettings = () => {
  const { setSettings, setThemePreference: setThemePreferenceStore, settings } = useSettingsStore();

  const { data, isError, isFetching, refetch } = useGetSettings();

  useEffect(() => {
    if (data) {
      setSettings(data);
    }
  }, [data, setSettings]);

  const { isPending: isUpdating, mutate: updateSettings } = useUpdateSettings({
    onSuccess: () => refetch(),
  });

  const writePushNotificationKeys = (keys: PushNotificationSetting['key'][], isEnabled: boolean) => {
    const updatedPushNotifications = settings.pushNotifications.map(setting =>
      keys.includes(setting.key) ? { ...setting, isEnabled, isUpdating: true } : setting,
    );

    const updatedSettings = { ...settings, pushNotifications: sortPushNotificationSettings(updatedPushNotifications) };
    setSettings(updatedSettings);
    updateSettings(updatedSettings);
  };

  const onUpdatePushNotificationSetting = (key: PushNotificationSetting['key'], isEnabled: boolean) => {
    writePushNotificationKeys(getPushNotificationKeysToUpdate(key, isEnabled), isEnabled);
  };

  const onToggleAllPushNotifications = (group: PushNotificationGroup, isEnabled: boolean) => {
    writePushNotificationKeys(getPushNotificationGroupKeys(group), isEnabled);
  };

  const setThemePreference = (themePreference: ThemePreference) => {
    setThemePreferenceStore(themePreference);
  };

  const updateTimeZoneSetting = (timeZone: TimeZone) => {
    const updatedSettings = { ...settings, preferences: { ...settings.preferences, timeZone } };
    setSettings(updatedSettings);
    updateSettings(updatedSettings);
  };

  return {
    fetchSettings,
    isError,
    isLoading: !settings && isFetching,
    isUpdating,
    onToggleAllPushNotifications,
    onUpdatePushNotificationSetting,
    refetch,
    setThemePreference,
    settings,
    updateTimeZoneSetting,
  };
};
